<template>
  <div class="document-viewer">
    <div ref="contentScroller" class="document-content-scroller" @scroll="updateActiveHeading">
      <article v-if="isHtml && htmlSrcdoc" class="document-html-frame">
        <iframe
          ref="htmlFrame"
          :srcdoc="htmlSrcdoc"
          :title="title || 'HTML 文档预览'"
          @load="onHtmlFrameLoad"
        ></iframe>
      </article>

      <article
        v-else-if="hasRichContent"
        ref="contentArticle"
        class="document-rich-content"
        v-html="safeHtml"
      ></article>

      <div v-else-if="isDoc && previewUrl" class="document-preview-frame">
        <iframe :src="previewUrl" title="DOC/DOCX 文档预览" loading="lazy" @load="loading = false"></iframe>
        <div v-if="loading" class="document-preview-overlay">
          <span class="state-spinner" aria-hidden="true"></span>
          <strong>正在打开 DOC/DOCX 文档</strong>
          <span>正在加载文档预览，请稍候。</span>
        </div>
      </div>

      <div v-else class="document-preview-state">
        <strong>暂不支持此文档类型</strong>
        <span>当前仅支持 DOC、DOCX、Markdown 和 HTML 文档。</span>
        <a v-if="downloadUrl" class="document-download-link" :href="downloadUrl" target="_blank" rel="noopener noreferrer" download>下载原文件</a>
      </div>
    </div>

    <aside v-if="tocItems.length" class="document-toc" aria-label="文档目录">
      <div class="document-toc-title">目录</div>
      <nav class="document-toc-nav">
        <button
          v-for="item in tocItems"
          :key="item.id"
          class="document-toc-item"
          :class="{ active: activeHeading === item.id }"
          :style="{ paddingLeft: `${12 + (item.level - 1) * 14}px` }"
          type="button"
          @click="scrollToHeading(item.id)"
        >{{ item.text }}</button>
      </nav>
    </aside>

    <button
      v-if="tocItems.length"
      class="mobile-toc-trigger"
      type="button"
      aria-label="打开文档目录"
      @click="mobileTocOpen = true"
    >目录</button>
    <div v-if="mobileTocOpen" class="mobile-toc-overlay" @click.self="mobileTocOpen = false">
      <aside class="mobile-toc-panel" aria-label="文档目录">
        <div class="mobile-toc-header">
          <strong>文档目录</strong>
          <button type="button" aria-label="关闭文档目录" @click="mobileTocOpen = false">×</button>
        </div>
        <nav class="document-toc-nav">
          <button
            v-for="item in tocItems"
            :key="`mobile-${item.id}`"
            class="document-toc-item"
            :class="{ active: activeHeading === item.id }"
            :style="{ paddingLeft: `${12 + (item.level - 1) * 14}px` }"
            type="button"
            @click="scrollToHeading(item.id); mobileTocOpen = false"
          >{{ item.text }}</button>
        </nav>
      </aside>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { resolveDocumentResourceUrl } from '@/api/documents'

const MEDIA_TAGS = ['video', 'source']
const MEDIA_ATTRIBUTES = [
  'src',
  'type',
  'controls',
  'preload',
  'playsinline',
  'muted',
  'loop',
  'poster',
  'width',
  'height'
]

const props = defineProps({
  document: { type: Object, default: () => ({}) },
  previewUrl: { type: String, default: '' },
  downloadUrl: { type: String, default: '' },
  title: { type: String, default: '' }
})

const loading = ref(true)
const contentScroller = ref(null)
const contentArticle = ref(null)
const htmlFrame = ref(null)
let htmlResizeObserver
const activeHeading = ref('')
const mobileTocOpen = ref(false)
const documentType = computed(() => normalizeDocumentType(props.document))
const isMarkdown = computed(() => documentType.value === 'markdown')
const isHtml = computed(() => documentType.value === 'html')
const isDoc = computed(() => documentType.value === 'doc')
const sourceHtml = computed(() => {
  const markdown = firstString(props.document.contentMarkdown, props.document.markdown)
  const html = firstString(props.document.contentHtml, props.document.html, props.document.content?.html)
  if (isMarkdown.value) return marked.parse(markdown || '', { breaks: true, gfm: true })
  if (html) return normalizeHtmlDocument(html)
  return ''
})
const htmlSrcdoc = computed(() => {
  if (!isHtml.value) return ''
  const html = firstString(props.document.contentHtml, props.document.html, props.document.content?.html)
  // The share shell owns the browser/page title; do not expose the source
  // document's <title> element in the embedded document.
  return html
    .replace(/^\uFEFF?\s*[^<]+?(?=\s*<(?:style|html|head|body|main|div|section|article)\b)/i, '')
    .replace(/<title\b[^>]*>[\s\S]*?<\/title\s*>/gi, '')
})
const hasRichContent = computed(() => Boolean(sourceHtml.value) && (isMarkdown.value || isHtml.value || isDoc.value))
const preparedContent = computed(() => prepareContent(sourceHtml.value, props.document.toc))
const safeHtml = computed(() => preparedContent.value.html || '<p class="empty-content">暂无文档内容</p>')
const tocItems = computed(() => preparedContent.value.toc)

watch([() => props.previewUrl, isDoc], () => {
  loading.value = true
}, { immediate: true })

watch(safeHtml, async () => {
  await nextTick()
  activeHeading.value = tocItems.value[0]?.id || ''
  updateActiveHeading()
})

onMounted(() => {
  nextTick(updateActiveHeading)
})

/** 处理 HTML iframe 加载，完成高度同步和文档内链接增强。 */
function onHtmlFrameLoad() {
  resizeHtmlFrame()
  bindHtmlFrameInteractions()
  htmlResizeObserver?.disconnect()
  const body = htmlFrame.value?.contentDocument?.body
  if (body && typeof ResizeObserver !== 'undefined') {
    htmlResizeObserver = new ResizeObserver(resizeHtmlFrame)
    htmlResizeObserver.observe(body)
  }
}

/** 根据嵌入文档实际内容同步 iframe 高度。 */
function resizeHtmlFrame() {
  const frame = htmlFrame.value
  const doc = frame?.contentDocument
  if (!frame || !doc) return
  frame.style.height = '0px'
  frame.style.height = `${Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight || 0)}px`
}

/** 为可信 HTML 文档恢复外链和锚点链接的点击行为。 */
function bindHtmlFrameInteractions() {
  const doc = htmlFrame.value?.contentDocument
  if (!doc) return
  doc.querySelectorAll('a[href]').forEach(link => {
    const href = link.getAttribute('href') || ''
    if (!href) return
    if (href.startsWith('#')) {
      link.addEventListener('click', event => {
        const target = doc.getElementById(decodeURIComponent(href.slice(1)))
        if (!target) return
        event.preventDefault()
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
      return
    }
    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noopener noreferrer')
  })
}

onBeforeUnmount(() => {
  htmlResizeObserver?.disconnect()
  activeHeading.value = ''
})

function firstString(...values) {
  return values.find(value => typeof value === 'string' && value.trim()) || ''
}

function normalizeDocumentType(documentValue) {
  const rawType = documentValue?.documentType
  const type = typeof rawType === 'object'
    ? String(rawType?.value || rawType?.name || '')
    : String(rawType || '')
  const fileName = String(documentValue?.originalFileName || '').toLowerCase()
  const extension = fileName.includes('.') ? fileName.split('.').pop() : ''
  const value = type.toLowerCase()
  if (value.includes('markdown') || value === 'md' || ['md', 'markdown'].includes(extension)) return 'markdown'
  if (value.includes('html') || value === 'htm' || ['html', 'htm'].includes(extension)) return 'html'
  if (value === 'doc' || value === 'docx' || extension === 'doc' || extension === 'docx') return 'doc'
  return value
}

function normalizeHtmlDocument(source) {
  const parser = new DOMParser()
  const parsed = parser.parseFromString(String(source || ''), 'text/html')
  parsed.querySelectorAll('script, iframe, object, embed, form, input, button, base, link, meta').forEach(element => element.remove())
  parsed.querySelectorAll('*').forEach(element => {
    Array.from(element.attributes).forEach(attribute => {
      const name = attribute.name.toLowerCase()
      const value = String(attribute.value || '').trim()
      if (name.startsWith('on') || ['src', 'href', 'action', 'formaction', 'xlink:href'].includes(name)
        && /^(javascript|vbscript|data:text\/html)/i.test(value)) {
        element.removeAttribute(attribute.name)
      }
    })
    if (element.tagName === 'A') {
      const href = element.getAttribute('href') || ''
      if (href && !href.startsWith('#')) {
        element.setAttribute('target', '_blank')
        element.setAttribute('rel', 'noopener noreferrer nofollow')
      }
    }
  })
  const css = Array.from(parsed.querySelectorAll('style')).map(style => style.textContent || '').join('\n')
  const body = parsed.body?.innerHTML || ''
  return `${css ? `<style>${sanitizeCss(css)}</style>` : ''}${body}`
}

function prepareContent(source, backendToc) {
  if (!source) return { html: '', toc: [] }
  const sanitized = DOMPurify.sanitize(String(source), {
    ADD_ATTR: ['style', 'target', 'rel', 'colspan', 'rowspan', ...MEDIA_ATTRIBUTES],
    ADD_TAGS: ['figure', 'figcaption', 'section', 'article', 'main', 'aside', 'style', ...MEDIA_TAGS],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'formaction']
  })
  const parser = new DOMParser()
  const parsed = parser.parseFromString(`<body>${sanitized}</body>`, 'text/html')
  const headings = Array.from(parsed.body.querySelectorAll('h1, h2, h3, h4, h5, h6'))
  const provided = Array.isArray(backendToc) ? backendToc : []
  const toc = []
  let tocIndex = 0
  headings.forEach((heading, index) => {
    const text = heading.textContent?.trim() || ''
    if (!text) return
    const providedItem = provided[tocIndex++]
    const headingLevel = Number(heading.tagName.substring(1) || 1)
    if (headingLevel > 3) return
    const id = String(providedItem?.id || heading.id || `document-heading-${index + 1}`)
    const level = Number(providedItem?.level || headingLevel)
    heading.id = id
    toc.push({ id, text: String(providedItem?.text || text), level: Math.min(3, Math.max(1, level)) })
  })
  resolveContentFileUrls(parsed.body)
  return { html: parsed.body.innerHTML, toc }
}

function resolveContentFileUrls(root) {
  root.querySelectorAll('a[href], img[src], video[src], video[poster], source[src]').forEach(element => {
    const attributeName = element.hasAttribute('href')
      ? 'href'
      : element.hasAttribute('poster') ? 'poster' : 'src'
    const value = element.getAttribute(attributeName) || ''
    let parsedUrl
    try {
      parsedUrl = new URL(value, window.location.origin)
    } catch {
      return
    }
    if (!/^\/app\/content\/file\/\d+$/.test(parsedUrl.pathname)) return
    if (value.startsWith('/app/content/file/')) {
      element.setAttribute(attributeName, resolveDocumentResourceUrl(value))
    }
    element.dataset.contentFile = 'true'
  })
}

function scrollToHeading(id) {
  if (isHtml.value) {
    const heading = htmlFrame.value?.contentDocument?.getElementById(id)
    heading?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    if (heading) activeHeading.value = id
    return
  }
  const heading = contentArticle.value?.querySelector(`[id="${escapeSelectorValue(id)}"]`)
  if (!heading || !contentScroller.value) return
  const scrollerRect = contentScroller.value.getBoundingClientRect()
  const headingRect = heading.getBoundingClientRect()
  contentScroller.value.scrollTo({
    top: contentScroller.value.scrollTop + headingRect.top - scrollerRect.top - 24,
    behavior: 'smooth'
  })
  activeHeading.value = id
}

function updateActiveHeading() {
  if (!contentArticle.value || !contentScroller.value || !tocItems.value.length) return
  const top = contentScroller.value.getBoundingClientRect().top + 32
  let current = tocItems.value[0].id
  for (const item of tocItems.value) {
    const heading = contentArticle.value.querySelector(`[id="${escapeSelectorValue(item.id)}"]`)
    if (heading && heading.getBoundingClientRect().top <= top) current = item.id
  }
  activeHeading.value = current
}

function escapeSelectorValue(value) {
  return String(value).replace(/(["\\])/g, '\\$1')
}

function sanitizeCss(value) {
  return String(value || '')
    .replace(/@import(?:[^;{]|;)/gi, '')
    .replace(/expression\s*\(/gi, 'void(')
    .replace(/behavior\s*:/gi, 'disabled-behavior:')
    .replace(/-moz-binding\s*:/gi, 'disabled-binding:')
    .replace(/(javascript|vbscript)\s*:/gi, 'void:')
}
</script>

<style scoped>
.document-viewer { display: flex; min-height: 0; flex: 1; overflow: hidden; color: #1f2937; background: #fff; }
.document-content-scroller { min-width: 0; min-height: 0; flex: 1; overflow: auto; background: #fff; }
.document-rich-content { width: min(100%, 960px); min-height: 100%; margin: 0 auto; padding: 32px 28px 48px; color: #1f2937; background: #fff; font-size: 16px; line-height: 1.8; overflow-wrap: anywhere; }
.document-html-frame { min-width: 0; min-height: 100%; flex: 1; margin: 0; background: #fff; }
.document-html-frame iframe { display: block; width: 100%; min-height: calc(100vh - 56px); border: 0; background: #fff; }
.document-rich-content :deep(h1), .document-rich-content :deep(h2), .document-rich-content :deep(h3), .document-rich-content :deep(h4), .document-rich-content :deep(h5), .document-rich-content :deep(h6) { scroll-margin-top: 24px; margin: 1.5em 0 .7em; color: #111827; line-height: 1.3; }
.document-rich-content :deep(h1) { font-size: clamp(28px, 4vw, 40px); }
.document-rich-content :deep(h2) { font-size: clamp(23px, 3vw, 32px); }
.document-rich-content :deep(h3) { font-size: 23px; }
.document-rich-content :deep(h4), .document-rich-content :deep(h5), .document-rich-content :deep(h6) { font-size: 19px; }
.document-rich-content :deep(p), .document-rich-content :deep(ul), .document-rich-content :deep(ol), .document-rich-content :deep(blockquote), .document-rich-content :deep(pre), .document-rich-content :deep(table), .document-rich-content :deep(figure) { margin: 1em 0; }
.document-rich-content :deep(ul), .document-rich-content :deep(ol) { padding-left: 1.7em; }
.document-rich-content :deep(a) { color: #0f766e; text-decoration: underline; text-underline-offset: 3px; }
.document-rich-content :deep(a[data-content-file='true']) { display: inline-flex; max-width: 100%; min-height: 38px; align-items: center; margin: 4px 0; padding: 0 12px; border: 1px solid #99f6e4; border-radius: 8px; color: #0f766e; background: #f0fdfa; font-weight: 600; text-decoration: none; }
.document-rich-content :deep(blockquote) { border-left: 3px solid #14b8a6; padding: .7em 1em; color: #4b5563; background: #f8fafc; }
.document-rich-content :deep(code) { padding: .15em .35em; border-radius: 4px; color: #9a3412; background: #fff7ed; font-size: .9em; }
.document-rich-content :deep(pre) { overflow: auto; padding: 16px; border: 1px solid #e5e7eb; border-radius: 6px; background: #f8fafc; }
.document-rich-content :deep(pre code) { padding: 0; background: transparent; }
.document-rich-content :deep(img) { display: block; max-width: 100%; height: auto; margin: 18px auto; border-radius: 8px; background: #fff; }
.document-rich-content :deep(video) { display: block; width: 100%; max-width: 100%; height: auto; margin: 18px 0; overflow: hidden; border-radius: 10px; background: #000; }
.document-rich-content :deep(table) { width: 100%; border-collapse: collapse; color: #1f2937; }
.document-rich-content :deep(th), .document-rich-content :deep(td) { padding: 10px 12px; border: 1px solid #d1d5db; text-align: left; }
.document-rich-content :deep(th) { color: #111827; background: #f3f4f6; }
.document-preview-frame { position: relative; min-height: 100%; background: #fff; }
.document-preview-frame iframe { width: 100%; height: 100%; min-height: 600px; border: 0; background: #fff; }
.document-preview-overlay { position: absolute; inset: 0; display: grid; place-items: center; align-content: center; gap: 10px; color: #64748b; background: rgba(255,255,255,.94); text-align: center; }
.document-preview-overlay strong { color: #111827; font-size: 17px; }
.document-preview-state { display: grid; min-height: 100%; place-items: center; align-content: center; gap: 10px; padding: 28px; color: #64748b; background: #fff; text-align: center; }
.document-preview-state strong { color: #111827; font-size: 17px; }
.document-download-link { display: inline-flex; min-height: 40px; align-items: center; justify-content: center; margin-top: 8px; padding: 0 14px; border: 1px solid #99f6e4; border-radius: 6px; color: #0f766e; background: #f0fdfa; text-decoration: none; }
.document-toc { width: 260px; min-width: 220px; max-height: 100%; overflow: auto; padding: 24px 12px 24px 0; border-left: 1px solid #eef2f7; background: #fff; }
.document-toc-title { padding: 0 12px 10px; color: #111827; font-size: 13px; font-weight: 700; }
.document-toc-nav { display: grid; gap: 2px; }
.document-toc-item { display: block; width: 100%; border: 0; border-left: 2px solid transparent; padding-top: 7px; padding-right: 10px; padding-bottom: 7px; color: #64748b; background: transparent; font-size: 13px; line-height: 1.45; text-align: left; cursor: pointer; overflow-wrap: anywhere; }
.document-toc-item:hover { color: #0f766e; background: #f8fafc; }
.document-toc-item.active { border-left-color: #0f766e; color: #0f766e; background: #f0fdfa; font-weight: 700; }
.mobile-toc-trigger, .mobile-toc-overlay { display: none; }
.state-spinner { width: 30px; height: 30px; border: 2px solid #ccfbf1; border-top-color: #0f766e; border-radius: 50%; animation: document-spin .8s linear infinite; }
.empty-content { color: #64748b; }
@keyframes document-spin { to { transform: rotate(360deg); } }
@media (max-width: 900px) { .document-toc { width: 220px; min-width: 190px; } }
@media (max-width: 640px) {
  .document-toc { display: none; }
  .mobile-toc-trigger { position: fixed; right: 14px; bottom: 18px; z-index: 30; display: inline-flex; min-width: 52px; height: 44px; align-items: center; justify-content: center; border: 1px solid #0f766e; border-radius: 22px; color: #fff; background: #0f766e; box-shadow: 0 8px 20px rgba(15,118,110,.28); font-size: 13px; font-weight: 700; cursor: pointer; }
  .mobile-toc-overlay { position: fixed; z-index: 40; inset: 0; display: flex; justify-content: flex-end; background: rgba(15,23,42,.42); }
  .mobile-toc-panel { width: min(86vw, 340px); height: 100%; overflow: auto; padding: 18px 12px calc(24px + env(safe-area-inset-bottom)); background: #fff; box-shadow: -12px 0 32px rgba(15,23,42,.18); }
  .mobile-toc-header { display: flex; align-items: center; justify-content: space-between; padding: 0 6px 14px 12px; border-bottom: 1px solid #eef2f7; color: #111827; }
  .mobile-toc-header button { width: 32px; height: 32px; border: 0; color: #64748b; background: transparent; font-size: 24px; line-height: 1; cursor: pointer; }
  .mobile-toc-panel .document-toc-nav { padding-top: 10px; }
  .document-rich-content { padding: 20px 16px 36px; font-size: 15px; }
  .document-rich-content :deep(img) { margin: 14px auto; border-radius: 6px; }
  .document-rich-content :deep(video) { margin: 14px 0; border-radius: 8px; }
  .document-rich-content :deep(a[data-content-file='true']) { min-height: 40px; }
}
</style>
